import React from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { FaLinkedin, FaArrowRight, FaRocket } from 'react-icons/fa';
import PageTransition from '../components/PageTransition';
import { fadeInUp, staggerContainer, viewport } from '../utils/animations';

const FOUNDERS = [
  {
    id: 1,
    name: 'Utkarsh Gauniyal',
    title: 'Co-Founder',
    image: '/utkarsh.jpg',
    linkedin: 'https://www.linkedin.com/in/utkarsh-gauniyal/',
    quote: 'Building technology that creates lasting impact.',
  },
  {
    id: 2,
    name: 'Akshaya Panda',
    title: 'Co-Founder',
    image: '/Akshaya.jpg',
    linkedin: 'https://www.linkedin.com/in/akshaya-panda-948457206/',
    quote: 'Engineering excellence at every layer of the stack.',
  },
  {
    id: 3,
    name: 'Deeksha Pandey',
    title: 'Co-Founder',
    image: '/Deeksha.jpg',
    linkedin: 'https://www.linkedin.com/in/deeksha-pandey-51021a220/',
    quote: 'Great products start with empathy and clarity.',
  },
];

const FounderCard = ({ founder, index }) => (
  <motion.div
    initial={{ opacity: 0, y: 60 }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true, margin: '-80px' }}
    transition={{ duration: 0.7, delay: index * 0.15, ease: [0.25, 0.1, 0.25, 1] }}
    whileHover={{ y: -10, transition: { duration: 0.3 } }}
    className="group relative"
  >
    {/* Card */}
    <div className="relative overflow-hidden rounded-3xl bg-gray-900 border border-gray-800/60 group-hover:border-gray-600/80 transition-all duration-500 shadow-2xl shadow-black/40">

      {/* Photo */}
      <div className="relative h-[420px] overflow-hidden">
        <img
          src={founder.image}
          alt={founder.name}
          className="w-full h-full object-cover object-top transition-transform duration-700 group-hover:scale-105"
        />
        {/* Bottom gradient */}
        <div className="absolute inset-0 bg-gradient-to-t from-gray-950 via-gray-950/30 to-transparent" />

        {/* LinkedIn hover button */}
        <motion.a
          href={founder.linkedin}
          target="_blank"
          rel="noopener noreferrer"
          whileHover={{ scale: 1.1 }}
          className="absolute top-5 right-5 w-10 h-10 bg-black/50 backdrop-blur-sm border border-white/20 rounded-full flex items-center justify-center text-white opacity-0 group-hover:opacity-100 transition-all duration-300 hover:bg-white hover:text-gray-900"
          onClick={(e) => e.stopPropagation()}
        >
          <FaLinkedin className="text-sm" />
        </motion.a>

        {/* Name block overlaid on photo */}
        <div className="absolute bottom-0 left-0 right-0 p-7">
          <div className="flex items-center gap-2 mb-2">
            <span className="w-6 h-px bg-white/60" />
            <span className="text-xs font-bold uppercase tracking-[0.2em] text-white/70">{founder.title}</span>
          </div>
          <h3 className="text-white font-black text-2xl leading-tight tracking-tight">
            {founder.name}
          </h3>
        </div>
      </div>

      {/* Quote strip */}
      <div className="px-7 py-5 border-t border-gray-800/60 bg-gray-900/80 backdrop-blur-sm">
        <p className="text-gray-400 text-sm leading-relaxed italic">
          &ldquo;{founder.quote}&rdquo;
        </p>
      </div>
    </div>

    {/* Glow effect on hover */}
    <div className="absolute -inset-px rounded-3xl bg-gradient-to-b from-white/5 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500 pointer-events-none" />
  </motion.div>
);

const Team = () => (
  <PageTransition>
    <div className="bg-transparent min-h-screen">

      {/* ── Hero ── */}
      <section className="relative pt-36 pb-16 overflow-hidden">
        <div className="absolute inset-0 grid-pattern opacity-30 pointer-events-none" />
        <div className="spotlight w-[700px] h-[700px] top-1/2 left-1/2 opacity-40" />

        <div className="container mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <motion.div
            variants={staggerContainer}
            initial="hidden"
            animate="visible"
            className="text-center max-w-4xl mx-auto"
          >
            <motion.div variants={fadeInUp} className="inline-flex mb-6">
              <div className="flex items-center gap-2 px-4 py-2 bg-gray-900/60 backdrop-blur-sm border border-gray-700 rounded-full badge-glow">
                <span className="w-1.5 h-1.5 bg-emerald-400 rounded-full animate-pulse" />
                <span className="text-xs font-semibold tracking-widest text-gray-300 uppercase">The Founders</span>
              </div>
            </motion.div>

            <motion.h1
              variants={fadeInUp}
              className="text-5xl md:text-6xl lg:text-7xl font-black mb-6 leading-tight"
            >
              <span className="text-white">The Minds</span>
              <br />
              <span className="animated-gradient-text">Behind Zynexis</span>
            </motion.h1>

            <motion.p
              variants={fadeInUp}
              className="text-gray-400 text-lg md:text-xl leading-relaxed max-w-2xl mx-auto"
            >
              Three builders with one shared obsession — creating software that moves the needle
              for businesses that dare to think differently.
            </motion.p>
          </motion.div>
        </div>
      </section>

      {/* ── Founders Grid ── */}
      <section className="relative py-16 pb-28">
        <div className="absolute inset-0 dot-pattern opacity-20 pointer-events-none" />
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-7 max-w-5xl mx-auto">
            {FOUNDERS.map((founder, i) => (
              <FounderCard key={founder.id} founder={founder} index={i} />
            ))}
          </div>
        </div>
      </section>

      {/* ── Values strip ── */}
      <section className="border-y border-gray-800/60 py-14">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="grid grid-cols-1 md:grid-cols-3 gap-8 text-center"
          >
            {[
              { value: 'Client-First', sub: 'Every decision starts with your outcome' },
              { value: 'Ship Fast', sub: 'Move quickly without cutting corners' },
              { value: 'Own It', sub: 'Full accountability from day one to launch' },
            ].map((v) => (
              <div key={v.value} className="flex flex-col items-center gap-2">
                <div className="text-xl font-black text-white">{v.value}</div>
                <div className="text-sm text-gray-500">{v.sub}</div>
              </div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* ── CTA ── */}
      <section className="relative py-28 overflow-hidden">
        <div className="spotlight w-96 h-96 top-1/2 left-1/2" />
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 relative z-10 text-center">
          <motion.div variants={staggerContainer} initial="hidden" whileInView="visible" viewport={viewport}>
            <motion.div variants={fadeInUp} className="inline-flex mb-6">
              <div className="flex items-center gap-2 px-4 py-2 bg-gray-900/60 backdrop-blur-sm border border-gray-700 rounded-full badge-glow">
                <FaRocket className="text-gray-400 text-xs" />
                <span className="text-xs font-semibold tracking-widest text-gray-300 uppercase">Join Us</span>
              </div>
            </motion.div>

            <motion.h2
              variants={fadeInUp}
              className="text-4xl md:text-5xl font-black text-white mb-5"
            >
              Ready to build something<br />
              <span className="animated-gradient-text">extraordinary?</span>
            </motion.h2>

            <motion.p variants={fadeInUp} className="text-gray-400 text-lg mb-10 max-w-xl mx-auto">
              We're always looking for brilliant people to join our mission.
              Come shape the future with us.
            </motion.p>

            <motion.div variants={fadeInUp} className="flex flex-wrap justify-center gap-4">
              <Link
                to="/careers"
                className="group flex items-center gap-2 px-8 py-4 bg-white text-gray-950 font-bold rounded-xl hover:bg-gray-100 transition-all duration-300 hover:-translate-y-1 shine-hover"
              >
                View Open Positions
                <FaArrowRight className="group-hover:translate-x-1 transition-transform duration-300" />
              </Link>
              <Link
                to="/contact"
                className="group flex items-center gap-2 px-8 py-4 border border-gray-700 text-gray-300 font-semibold rounded-xl hover:border-gray-500 hover:text-white transition-all duration-300 hover:-translate-y-1"
              >
                Get in Touch
              </Link>
            </motion.div>
          </motion.div>
        </div>
      </section>

    </div>
  </PageTransition>
);

export default Team;